import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GripVertical, Pencil, Loader2, Save, LayoutGrid, List, ImageOff, Search, Trash2 } from 'lucide-react';
import { formatCLP } from '@/lib/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Drag-and-drop reorderable product grid.
 * Props:
 *  - token: admin bearer token
 *  - onEdit(product): opens the edit modal
 *  - onDeleted(): optional callback after a product is deleted
 *  - reloadKey: change this prop to force a reload (e.g., after external save)
 */
export default function ProductCardGrid({ token, onEdit, onDeleted, reloadKey = 0 }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/products`);
      // Backend already sorts by display_order,name
      setProducts(data || []);
      setDirty(false);
    } catch (e) { toast.error('Error al cargar productos'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const removeProduct = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.\n\nLa URL pública /productos/${p.slug} será removida del sitemap.xml automáticamente.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Producto eliminado');
      setProducts((prev) => prev.filter(x => x.id !== p.id));
      if (onDeleted) onDeleted();
      // Regenerate sitemap (best-effort, non-blocking)
      try {
        await axios.post(`${API}/admin/sitemap/regenerate`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Sitemap regenerado');
      } catch (_) { /* non-blocking */ }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`Error al eliminar: ${detail}`);
    }
  };

  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();
  const filtered = products.filter(p => {
    if (category !== 'all' && p.category !== category) return false;
    if (q) {
      const n = q.toLowerCase();
      if (!`${p.name} ${p.brand || ''} ${p.slug}`.toLowerCase().includes(n)) return false;
    }
    return true;
  });

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    // We reorder only within the FILTERED view.
    // Move in the main `products` array while respecting filter view indexes.
    const draggedProd = filtered[dragIdx];
    const targetProd = filtered[idx];
    if (!draggedProd || !targetProd) return;
    const src = products.findIndex(p => p.id === draggedProd.id);
    const dst = products.findIndex(p => p.id === targetProd.id);
    if (src === -1 || dst === -1) return;
    const next = [...products];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setProducts(next);
    setDragIdx(idx);
    setDirty(true);
  };
  const onDragEnd = () => setDragIdx(null);

  const save = async () => {
    setSaving(true);
    try {
      const items = products.map((p, i) => ({ id: p.id, order: i }));
      await axios.put(`${API}/admin/products-reorder`, { items }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${items.length} productos reordenados`);
      setDirty(false);
      await load();
    } catch (e) { toast.error('Error al guardar orden'); }
    setSaving(false);
  };

  const resolveImg = (url) => url?.startsWith('/api/') ? `${BACKEND}${url}` : url;

  return (
    <div data-testid="product-card-grid">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 border-b border-slate-200">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, marca o slug…"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="grid-search"
          />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="grid-category-filter">
          <option value="all">Todas las categorías</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0EA5E9] text-white text-sm font-semibold hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed shadow"
          data-testid="grid-save-order"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {dirty ? `Guardar orden (${products.length})` : 'Orden guardado'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9] mx-auto" /></div>
      ) : (
        <>
          <div className="mb-3 text-xs text-slate-500">
            💡 <strong>Arrastra las tarjetas</strong> para reordenarlas. El orden se refleja en <code className="px-1 py-0.5 bg-slate-100 rounded">/hardware</code> y <code className="px-1 py-0.5 bg-slate-100 rounded">/soluciones</code>. Mostrando {filtered.length} de {products.length}.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((p, idx) => (
              <div
                key={p.id}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDragEnd={onDragEnd}
                className={`group relative bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-[#0EA5E9]/50 transition-all cursor-move ${dragIdx === idx ? 'opacity-40 ring-2 ring-[#0EA5E9]' : ''}`}
                data-testid={`grid-card-${p.slug}`}
              >
                <div className="absolute top-2 left-2 z-10 w-6 h-6 rounded-md bg-black/60 backdrop-blur text-white/80 flex items-center justify-center shadow">
                  <GripVertical className="w-3.5 h-3.5" />
                </div>
                <button
                  type="button"
                  onClick={() => onEdit(p)}
                  className="absolute top-2 right-2 z-10 w-6 h-6 rounded-md bg-white/90 hover:bg-[#0EA5E9] hover:text-white text-[#0EA5E9] flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Editar"
                  data-testid={`grid-edit-${p.slug}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeProduct(p)}
                  className="absolute top-2 right-10 z-10 w-6 h-6 rounded-md bg-white/90 hover:bg-red-500 hover:text-white text-red-500 flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Eliminar"
                  data-testid={`grid-delete-${p.slug}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="aspect-square rounded-t-xl overflow-hidden bg-slate-100 flex items-center justify-center">
                  {p.image ? (
                    <img src={resolveImg(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-[#0EA5E9] truncate">{p.brand || p.category}</div>
                  <div className="text-xs font-semibold text-[#0A2540] line-clamp-2 leading-tight mt-0.5" style={{ minHeight: '2em' }}>{p.name}</div>
                  {p.price && (
                    <div className="text-[11px] font-bold text-[#0A2540] mt-1 tabular-nums">{formatCLP(p.price)}</div>
                  )}
                  {Array.isArray(p.solution_slugs) && p.solution_slugs.length > 0 && (
                    <div className="text-[9px] text-slate-400 mt-1 truncate" title={p.solution_slugs.join(', ')}>
                      📎 {p.solution_slugs.length} soluc.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
